/**
 * Générateur — Automatisme : le compas pour reporter une longueur
 * Attendu ID : 1023 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait utiliser le
 * compas comme outil de report de longueurs. »
 * Labels (cartes exerciseur) :
 *   - Le compas pour reporter une longueur
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * Un automatisme de GESTE : le compas transporte une longueur sans la
 * mesurer (fonction distincte du tracé de cercles). 4 formats (le V/F a été
 * retiré, décision prof 2026-08-21 : inadapté à un automatisme de geste) :
 *   - CONSTRUCTION interactive (report SIMPLE, arbitrage prof) : segment
 *     [AB] tracé + demi-droite d'origine O, l'élève trace le cercle de
 *     centre O et de rayon AB — correction géométrique (centre + rayon) ;
 *   - ORDONNER le programme du geste (3 sous-programmes : report simple,
 *     comparaison, report double) — chaque étape dépend de la précédente ;
 *   - QCM (3 sous-types de la flash), distracteurs nommés dont le
 *     contresens « on mesure à la règle » ;
 *   - DRAG : le récit du geste à trous (vocabulaire : écartement, pointe,
 *     arc, intersection), tuiles toutes distinctes.
 * CONVENTIONS du cadrage : jamais « 2 × AB » en notation sèche (toujours
 * « deux fois plus long »), le report jamais présenté comme une mesure.
 * Variété d'un domaine fermé : sous-types, lettres, formulations (assumé,
 * comme le 1012).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeOrdonner, makeProgrammeConstruction,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 1023;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Le compas pour reporter une longueur';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1023 : ${message}`);
};

/** Paires de lettres pour le segment + origine, sans collision. */
const LETTRES_SEGMENT = [['A', 'B'], ['C', 'D'], ['E', 'F'], ['G', 'H'], ['M', 'N'], ['R', 'S'], ['T', 'U']];
const LETTRES_ORIGINE = ['O', 'P', 'K', 'L'];

const tirerLettres = () => {
    const [g, d] = randomChoice(LETTRES_SEGMENT);
    const o = randomChoice(LETTRES_ORIGINE.filter((x) => x !== g && x !== d));
    return { g, d, o };
};

// ─── Format 1 : le report simple, au compas interactif ────────────────────

const PX_PER_CM = 30;
const VIEWBOX = { w: 720, h: 540 };

const genReportConstruction = () => {
    const { g, d, o } = tirerLettres();
    // le segment à reporter : longueur 3 à 6 cm, incliné pour ne pas être
    // parallèle à la demi-droite (le report n'est pas un décalque visuel)
    const lCm = randInt(6, 9) / 2;                       // 3 à 4,5 cm
    const rPx = Math.round(lCm * PX_PER_CM);
    // A et O placés pour que LES DEUX cercles (prise de mesure sur A, report
    // sur O) tiennent entiers dans le viewBox (A-PC-VIEWBOX)
    const angle = randomChoice([-35, -25, -15, 15, 25, 35]) * (Math.PI / 180);
    const ax = randInt(rPx + 30, rPx + 120);
    const ay = randInt(rPx + 25, rPx + 65);
    const bx = Math.round(ax + lCm * PX_PER_CM * Math.cos(angle));
    const by = Math.round(ay + lCm * PX_PER_CM * Math.sin(angle));
    garde(bx > 0 && bx < VIEWBOX.w && by > 0 && by < VIEWBOX.h, 'segment hors cadre');
    const ox = randInt(rPx + 40, rPx + 110);
    const oy = randInt(Math.max(rPx + 20, 340), VIEWBOX.h - rPx - 20);
    garde(ax - rPx >= 0 && ay - rPx >= 0 && ax + rPx <= VIEWBOX.w && ay + rPx <= VIEWBOX.h,
        'cercle de prise hors cadre');
    garde(ox - rPx >= 0 && ox + rPx <= VIEWBOX.w && oy - rPx >= 0 && oy + rPx <= VIEWBOX.h,
        'cercle de report hors cadre');
    // la longueur RÉELLE tracée (pixels arrondis), re-mesurée : le rayon
    // attendu est la distance AB effective, jamais la valeur théorique
    const rReel = Math.sqrt((bx - ax) ** 2 + (by - ay) ** 2);
    garde(Math.abs(rReel - rPx) <= 2, 'rayon incohérent avec le segment tracé');
    const lFr = String(lCm).replace('.', ',');
    const texte = `<p>Le compas sert aussi à <b>reporter</b> une longueur, en deux temps : `
        + `on prend l'écartement sur le segment, puis on le reporte depuis ${o}.</p>`;
    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: 12,
            viewBox: VIEWBOX,
            points: [
                { name: g, x: ax, y: ay },
                { name: d, x: bx, y: by },
                { name: o, x: ox, y: oy },
            ],
            preShapes: [
                { type: 'line', from: g, to: d },
                { type: 'ray', from: o, angleDeg: 0, length: VIEWBOX.w - ox - 40 },
            ],
            // TROIS temps (décision prof 2026-08-21) : la PRISE ne trace RIEN
            // (mode pointsOnly : cliquer les deux extrémités = piquer puis
            // ouvrir) et la mesure s'écrit SOUS le segment à la validation
            // (labelOnDone, ajout additif du composant, feu vert prof) ; le
            // REPORT trace le vrai cercle ; enfin l'élève PLACE le point
            // ${d}′ à l'intersection (idiome rayon nul + pointLabel).
            tasks: [
                {
                    id: 'prise',
                    kind: 'segment',
                    pointsOnly: true,
                    consigne: `<b>1. Prends la longueur :</b> pique la pointe sur ${g}, puis ouvre le `
                        + `compas jusqu'à ${d} (clique ${g}, puis ${d}).`,
                    color: '#2563eb',
                    labelOnDone: {
                        x: Math.round((ax + bx) / 2),
                        y: Math.max(ay, by) + 26,
                        text: `${g}${d} = ${lFr} cm`,
                    },
                    shape: { type: 'segment', p1: { x: ax, y: ay }, p2: { x: bx, y: by } },
                },
                {
                    id: 'report',
                    consigne: `<b>2. Reporte :</b> sans changer l'écartement, pique la pointe sur ${o} et `
                        + `trace le cercle de rayon ${lFr} cm.`,
                    color: '#f97316',
                    shape: { type: 'circle', cx: ox, cy: oy, r: rReel },
                },
                {
                    id: 'place',
                    consigne: `<b>3. Place le point ${d}′</b> à l'intersection du cercle et de la `
                        + `demi-droite : ${o}${d}′ = ${g}${d}.`,
                    color: '#1D9E75',
                    pointLabel: `${d}′`,
                    shape: { type: 'circle', cx: ox + rReel, cy: oy, r: 0 },
                },
            ],
        },
        texte,
        opts(L1),
    );
    payload._meta.params = { g, d, o, lCm };
    return payload;
};

// ─── Figures de contexte (imageUrl) pour ordonner et drag ─────────────────

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });
const NOIR = '#111827';

const svgPoint = (x, y, nom, dy = -8) =>
    `<circle cx="${x}" cy="${y}" r="3" fill="${NOIR}"/>`
    + `<text x="${x}" y="${y + dy}" font-size="14" font-weight="700" fill="${NOIR}" text-anchor="middle" font-family="system-ui,sans-serif">${nom}</text>`;

/** Segment [gd] incliné + demi-droite d'origine o (report, report double).
 *  INCLUSION stricte (exigence prof 2026-08-21) : points, traits ET
 *  étiquettes tiennent dans le viewBox, gardes qui throwent. */
const figureReport = (g, d, o) => {
    let ax = 40; let ay; let bx; let by;
    for (let essai = 0; essai < 30; essai++) {
        ay = randInt(56, 66);
        const lg = randInt(95, 135);
        const ang = randomChoice([-18, -10, 10, 18]) * (Math.PI / 180);
        bx = Math.round(ax + lg * Math.cos(ang));
        by = Math.round(ay + lg * Math.sin(ang));
        // étiquettes à y − 8 (haut de texte ≈ y − 20) et bas du segment au-
        // dessus de la demi-droite (y = 116)
        if (ay >= 24 && by >= 24 && ay <= 104 && by <= 104 && bx <= 310) break;
        bx = null;
    }
    garde(bx !== null && ay - 22 >= 0 && by - 22 >= 0 && Math.max(ay, by) <= 108 && bx <= 312,
        'figure report : élément hors cadre');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 150" width="320" role="img"`
        + ` aria-label="Un segment ${g}${d} et une demi-droite d'origine ${o}">`
        + `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="${NOIR}" stroke-width="2.2"/>`
        + svgPoint(ax, ay, g) + svgPoint(bx, by, d)
        + `<line x1="34" y1="116" x2="300" y2="116" stroke="${NOIR}" stroke-width="2.2"/>`
        + `<path d="M304 116 l-8 -4 v8 Z" fill="${NOIR}"/>`
        + svgPoint(34, 116, o, 22)
        + '</svg>';
    return elImage(dataUrl(svg));
};

/** Deux segments de longueurs nettement différentes (comparaison). */
const figureComparaison = (g, d, g2, d2) => {
    const l1 = randInt(90, 120);
    const l2 = l1 + randomChoice([-1, 1]) * randInt(35, 60);
    const seg = (x, y, l, n1, n2) =>
        `<line x1="${x}" y1="${y}" x2="${x + l}" y2="${y}" stroke="${NOIR}" stroke-width="2.2"/>`
        + svgPoint(x, y, n1) + svgPoint(x + l, y, n2);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 130" width="320" role="img"`
        + ` aria-label="Deux segments ${g}${d} et ${g2}${d2} de longueurs différentes">`
        + seg(40, 44, l1, g, d)
        + seg(40, 100, Math.max(50, l2), g2, d2)
        + '</svg>';
    return elImage(dataUrl(svg));
};

// ─── Format 2 : ordonner le programme du geste ────────────────────────────

const PROGRAMMES = {
    report: (g, d) => [
        `J'écarte le compas de ${g} à ${d} pour prendre la longueur ${g}${d}`,
        'Je pique la pointe sur l\'origine de la demi-droite',
        'Sans changer l\'écartement, je trace un arc qui coupe la demi-droite',
        'Le point d\'intersection est le point cherché',
    ],
    comparaison: (g, d, g2, d2) => [
        `Je règle l'écartement du compas sur le segment [${g}${d}]`,
        `Sans changer l'écartement, je pique la pointe sur une extrémité de [${g2}${d2}]`,
        `Je trace un arc près de l'autre extrémité de [${g2}${d2}]`,
        `Je regarde si l'arc tombe avant ou après cette extrémité : je sais lequel de [${g}${d}] et [${g2}${d2}] est le plus long`,
    ],
    double: (g, d) => [
        `J'écarte le compas de ${g} à ${d} pour prendre la longueur ${g}${d}`,
        'Je reporte cette longueur une première fois à partir de l\'origine de la demi-droite',
        'Je pique la pointe sur le point que je viens d\'obtenir',
        `Je reporte la longueur une seconde fois, dans le même sens : le point obtenu est deux fois plus loin que ${g}${d}`,
    ],
};

const CONSIGNES_ORDONNER = {
    report: 'Remets dans l\'ordre les étapes du report d\'une longueur au compas.',
    comparaison: 'Remets dans l\'ordre les étapes pour comparer deux segments au compas, sans règle graduée.',
    double: 'Remets dans l\'ordre les étapes pour construire un point deux fois plus loin, au compas.',
};

const genOrdonner = () => {
    const sousType = randomChoice(['report', 'comparaison', 'double']);
    const { g, d } = tirerLettres();
    const [g2, d2] = randomChoice(LETTRES_SEGMENT.filter(([x]) => x !== g));
    const etapes = PROGRAMMES[sousType](g, d, g2, d2);
    garde(new Set(etapes).size === etapes.length, 'ordonner : étapes dupliquées');
    // figure de contexte (demande prof 2026-08-21) : l'élève VOIT de quoi
    // parlent les étapes
    const figure = sousType === 'comparaison'
        ? figureComparaison(g, d, g2, d2)
        : figureReport(g, d, randomChoice(LETTRES_ORIGINE.filter((x) => x !== g && x !== d)));
    const payload = makeOrdonner(
        etapes,
        `<p><b>${CONSIGNES_ORDONNER[sousType]}</b></p>{{element:0}}`,
        { ...opts(L1), elements_additionnels: figure },
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, g, d };
    return payload;
};

// ─── Format 3 : QCM à erreurs nommées (3 sous-types de la flash) ──────────

const genQcm = () => {
    const { g, d, o } = tirerLettres();
    const sousType = randomChoice(['pointe', 'utilite', 'comparer']);
    let texte;
    let bonne;
    let dis;
    if (sousType === 'pointe') {
        texte = `<p><b>Pour reporter la longueur ${g}${d} sur une demi-droite d'origine ${o}, où pique-t-on la pointe du compas ?</b></p>`;
        bonne = `Sur ${o}, l'origine de la demi-droite`;
        dis = [
            { valeur: `Sur le point ${g}`, code: 'pointe_sur_extremite' },
            { valeur: `Sur le point ${d}`, code: 'pointe_sur_extremite' },
            { valeur: `Au milieu du segment [${g}${d}]`, code: 'pointe_au_milieu' },
        ];
    } else if (sousType === 'utilite') {
        texte = '<p><b>À quoi sert le compas, en dehors du tracé de cercles ?</b></p>';
        bonne = 'À reporter une longueur d\'un endroit à un autre, sans la mesurer';
        dis = [
            { valeur: 'À mesurer une longueur en centimètres', code: 'contresens_mesure' },
            { valeur: 'À tracer des droites parallèles', code: 'autre_outil' },
            { valeur: 'À vérifier qu\'un angle est droit', code: 'autre_outil' },
        ];
    } else {
        texte = '<p><b>Comment comparer les longueurs de deux segments, sans règle graduée ?</b></p>';
        bonne = 'On règle l\'écartement du compas sur l\'un, et on le reporte sur l\'autre';
        dis = [
            { valeur: 'On mesure les deux segments à la règle graduée', code: 'contresens_mesure' },
            { valeur: 'On compare les deux segments à l\'œil', code: 'estimation' },
            { valeur: 'On prolonge les deux segments jusqu\'à ce qu\'ils se croisent', code: 'geste_inoperant' },
        ];
    }
    const payload = makeQcm([bonne, ...dis.map((c) => c.valeur)], bonne, texte, opts(L1), false);
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

// ─── Format 5 : le récit du geste à trous (drag) ──────────────────────────

const RECITS = [
    (g, d) => `Je règle l'{{écartement}} du compas sur le segment [${g}${d}] ; je pique la {{pointe}} sur l'origine de la demi-droite ; je trace un {{arc}} ; son {{intersection}} avec la demi-droite donne le point cherché.`,
    (g, d) => `Pour comparer deux segments, je prends l'{{écartement}} de [${g}${d}] avec le compas ; je pique la {{pointe}} sur une extrémité de l'autre segment ; je trace un {{arc}} : je vois tout de suite lequel est le plus {{long}}.`,
    (g, d) => `Pour construire un point deux fois plus loin, je prends l'{{écartement}} ${g}${d} au compas ; je le reporte depuis l'origine ; je pique la {{pointe}} sur le point obtenu ; je reporte encore une fois, dans le même {{sens}}.`,
];

const genDrag = () => {
    const { g, d, o } = tirerLettres();
    const idx = randInt(0, RECITS.length - 1);
    const phrase = RECITS[idx](g, d);
    // figure de contexte (demande prof 2026-08-21) : récit 2 = comparaison
    // (deux segments), récits 1 et 3 = report (segment + demi-droite)
    const figure = idx === 1
        ? figureComparaison(g, d, ...randomChoice(LETTRES_SEGMENT.filter(([x]) => x !== g)))
        : figureReport(g, d, o);
    // distracteurs de vocabulaire : aucun n'est vrai dans aucun trou
    // (« cercle » est banni : « je trace un cercle » serait juste)
    const distracteurs = filtrerDistracteurs(phrase, shuffle(['mine', 'milieu', 'règle', 'graduation']).slice(0, 3));
    garde(distracteurs.length >= 2, 'drag : distracteurs filtrés');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète le récit du geste avec les bons mots.</b></p>{{element:0}}',
        { ...opts(L1), mode: 'drag', distracteurs, elements_additionnels: figure },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'domaine FERMÉ (un geste) : variété par sous-types, lettres et formulations, assumée comme le 1012',
        'jamais « 2 × AB » en notation sèche (toujours « deux fois plus loin ») ; le report jamais présenté comme une mesure',
        '« cercle » banni des distracteurs du drag : « je trace un cercle » serait mathématiquement juste',
    ],
    compas_qcm_unique: {
        support: '3 sous-types (pointe, utilité, comparaison), grilles fixes relues',
        fixes: {
            pointe_sur_extremite: 'piquer sur A ou B (confusion prise / report)',
            pointe_au_milieu: 'piquer au milieu du segment',
            contresens_mesure: 'réintroduire la règle graduée — LE contresens du report',
            autre_outil: 'une fonction d\'un autre instrument',
            estimation: 'comparer à l\'œil',
            geste_inoperant: 'un geste qui ne compare rien',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    compas_report_construction: genReportConstruction,
    compas_ordonner: genOrdonner,
    compas_qcm_unique: genQcm,
    compas_texte_trou_drag: genDrag,
};

const FORMAT_LABELS = {
    compas_report_construction: 'Construction',
    compas_ordonner: 'Ordonner',
    compas_qcm_unique: 'QCM',
    compas_texte_trou_drag: 'Drag',
};

export const CATEGORIES = [
    {
        id: 'compas',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
